import { Graphics, Sprite, Texture, TilingSprite } from 'pixi.js';
import type { Camera, Collectible, Guard, World } from '../world/types.js';
import { tex } from './assets.js';
import type { Stage } from './stage.js';

/** Sprite tracker tables — populated by buildStaticLayers, consumed by
 * the renderer (visibility + tint updates). Cleared at the start of
 * each level rebuild. */
export const intelSprites = new Map<Collectible, Sprite>();
export const cameraSprites = new Map<Camera, Sprite>();
export const guardSprites = new Map<Guard, Sprite>();
/** KO body sprites — separate from the standing guardSprites because
 * the lying-down pose lives in the `bodies` layer (under the player /
 * other guards) and uses a different texture entirely. */
export const guardBodySprites = new Map<Guard, Sprite>();
let _exitPadSprite: Sprite | null = null;
let _playerSprite: Sprite | null = null;
export function getExitPadSprite(): Sprite | null {
  return _exitPadSprite;
}
export function getPlayerSprite(): Sprite | null {
  return _playerSprite;
}

const LIGHT_TEXTURE_RADIUS = 110;

/** Build all layers that never change after world creation. */
export function buildStaticLayers(stage: Stage, world: World): void {
  buildFloor(stage, world);
  buildLights(stage, world);
  buildWallShadows(stage, world);
  buildWalls(stage, world);
  buildPropSprites(stage, world);
}

/**
 * Drop static asset Sprites into the relevant layers. They sit beneath
 * the dynamic Graphics (which is created later by initRenderer with the
 * '__dyn' label) so the dynamic overlay can layer animation on top.
 */
function buildPropSprites(stage: Stage, world: World): void {
  intelSprites.clear();
  cameraSprites.clear();
  guardSprites.clear();
  guardBodySprites.clear();
  _exitPadSprite = null;
  _playerSprite = null;

  // Player — single Sprite. Texture swapped each frame by the renderer
  // based on facing dir + walk-cycle phase. Anchored slightly below
  // centre so the character's "feet" sit on the world coordinate, with
  // body + head extending up — gives the 3/4 perspective sprite a
  // natural grounding when the camera moves. The renderer applies a
  // uniform scale based on the active texture's height to keep the
  // silhouette consistent across poses with varying aspect ratios.
  const playerSprite = new Sprite(tex('player.s_a'));
  playerSprite.anchor.set(0.5, 0.62);
  stage.layers.player.addChild(playerSprite);
  _playerSprite = playerSprite;

  // Exit pad — single Sprite at the extraction position. The source
  // PNG also ships with a solid white background, so we clip it to a
  // square slightly smaller than the sprite to hide the white border
  // (the artwork itself is roughly square, so we don't lose content).
  const padSprite = new Sprite(tex('exitPad'));
  padSprite.anchor.set(0.5);
  padSprite.position.set(world.exitPos.x, world.exitPos.y);
  const padSize = 38;
  padSprite.width = padSize;
  padSprite.height = padSize;
  const padMask = new Graphics();
  padMask.circle(world.exitPos.x, world.exitPos.y, padSize / 2).fill(0xffffff);
  stage.layers.exitPad.addChild(padMask);
  padSprite.mask = padMask;
  stage.layers.exitPad.addChild(padSprite);
  _exitPadSprite = padSprite;

  // Intel collectibles — one Sprite per item.
  for (const c of world.collectibles) {
    const s = new Sprite(tex('intel'));
    s.anchor.set(0.5);
    s.position.set(c.x, c.y);
    s.width = 22;
    s.height = 22;
    stage.layers.exitPad.addChild(s);
    intelSprites.set(c, s);
  }

  // Cameras — one Sprite per camera. Position fixed; the dynamic layer
  // still draws the cone + glitch overlays on top.
  for (const cam of world.cameras) {
    const s = new Sprite(tex('camera'));
    s.anchor.set(0.5);
    s.position.set(cam.x, cam.y);
    s.width = 22;
    s.height = 22;
    stage.layers.cameras.addChild(s);
    cameraSprites.set(cam, s);
  }

  // Guards — one Sprite per guard. Texture swapped each frame by the
  // renderer based on facing dir + walk-cycle. Anchored slightly
  // above-centre so the feet sit on the world coordinate. KO guards
  // hide their sprite and are drawn separately via the bodies layer.
  //
  // Source PNGs have varied aspect ratios (front/back ≈ 0.66,
  // sideways ≈ 0.88) — using width/height directly would stretch some
  // poses. The renderer sets a uniform scale per frame based on the
  // active texture's height (target 44 px) so the silhouette stays
  // consistent and width follows the natural pose ratio.
  for (const guard of world.guards) {
    const s = new Sprite(tex('guard.idle'));
    s.anchor.set(0.5, 0.62);
    s.position.set(guard.x, guard.y);
    stage.layers.guards.addChild(s);
    guardSprites.set(guard, s);

    // KO body sprite — sits in the bodies layer (drawn under live
    // guards / the player). Hidden by default; the renderer toggles
    // visibility based on guard.ko + hiddenInBox state.
    const body = new Sprite(tex('guard.ko'));
    body.anchor.set(0.5, 0.5);
    body.position.set(guard.x, guard.y);
    body.visible = false;
    stage.layers.bodies.addChild(body);
    guardBodySprites.set(guard, body);
  }
}

// How many world pixels one full tiling-texture repeat should cover.
// The source PNGs are seamless ~1254×1254. Tiling them across the
// whole world (e.g. 1280px wide) at native size ends up showing a
// single nearly-uniform image, which the CSS upscale then makes look
// soft/pixelated. We instead scale them down so one repeat covers
// 8 game tiles (256 px) — gives the floor distinct detail per tile
// without obvious seam patterns. Walls use a tighter scale so panels
// read at their actual size.
const FLOOR_TILE_PX = 256;
const WALL_TILE_PX = 128;

// Texture providers — wrap the asset module so the rest of the file
// reads with a stable name even if we swap the source later. Both
// textures get linear filtering with mipmaps explicitly set so the
// downsampled tiling looks sharp at any device pixel ratio.
function getFloorTexture(): Texture {
  const t = tex('floor');
  t.source.scaleMode = 'linear';
  t.source.autoGenerateMipmaps = true;
  return t;
}
function getWallTexture(): Texture {
  const t = tex('wall');
  t.source.scaleMode = 'linear';
  t.source.autoGenerateMipmaps = true;
  return t;
}

function buildFloor(stage: Stage, world: World): void {
  // 1. Two-tone checker base, batched per shade — keeps the cool game-y
  // feel even after the texture overlay lands on top.
  const a = new Graphics();
  const b = new Graphics();
  for (let y = 0; y < world.MH; y++) {
    const row = world.MAP[y]!;
    for (let x = 0; x < world.MW; x++) {
      if (row[x] === '#') continue;
      const target = (x + y) & 1 ? a : b;
      target.rect(x * world.TILE, y * world.TILE, world.TILE, world.TILE);
    }
  }
  a.fill(0x0f1320);
  b.fill(0x10151f);

  // 2. Concrete texture — TilingSprite covering the whole world, masked
  // to floor-tile area. Drives most of the floor look; the alternating
  // tile fills underneath only show through subtly at low alpha.
  const floorTex = getFloorTexture();
  const overlay = new TilingSprite({
    texture: floorTex,
    width: world.WORLD_W,
    height: world.WORLD_H,
  });
  // Repeat the texture every FLOOR_TILE_PX game pixels rather than at
  // its native size — see comment by FLOOR_TILE_PX for the rationale.
  const fScale = FLOOR_TILE_PX / floorTex.width;
  overlay.tileScale.set(fScale, fScale);
  overlay.alpha = 1;

  const overlayMask = new Graphics();
  for (let y = 0; y < world.MH; y++) {
    const row = world.MAP[y]!;
    for (let x = 0; x < world.MW; x++) {
      if (row[x] === '#') continue;
      overlayMask.rect(x * world.TILE, y * world.TILE, world.TILE, world.TILE);
    }
  }
  overlayMask.fill(0xffffff);
  overlay.mask = overlayMask;

  // 3. Tile boundary grid — barely visible, gives a sense of grid scale.
  const grid = new Graphics();
  for (let y = 0; y <= world.MH; y++) {
    grid.moveTo(0, y * world.TILE).lineTo(world.WORLD_W, y * world.TILE);
  }
  for (let x = 0; x <= world.MW; x++) {
    grid.moveTo(x * world.TILE, 0).lineTo(x * world.TILE, world.WORLD_H);
  }
  grid.stroke({ color: 0x1a2030, alpha: 0.2, width: 1 });

  stage.layers.floor.addChild(a, b, overlay, overlayMask, grid);
}

let cachedLightTexture: Texture | null = null;

function getLightTexture(): Texture {
  if (cachedLightTexture) return cachedLightTexture;
  // Render the halo at 2× native resolution then downsample with
  // linear filtering — gives crisper anti-aliased edges than drawing
  // straight at native size, which can show as pixel-stepped corners
  // once the global AdvancedBloomFilter amplifies them.
  const r = LIGHT_TEXTURE_RADIUS;
  const SS = 2; // supersample factor
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = r * 2 * SS;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.scale(SS, SS);
    // Clip to a circle so the canvas RGBA is a true (0,0,0,0) outside
    // the disc — without this, the gradient's last-stop warm RGB
    // remains in the corner texels (alpha 0), and mipmap downsampling
    // bleeds it into nearby pixels, producing a visible square halo.
    ctx.beginPath();
    ctx.arc(r, r, r, 0, Math.PI * 2);
    ctx.clip();

    // Single soft warm wash — no bright inner core. The lamp.png
    // fixture itself is what should glow; this layer is just the
    // ambient floor tint that radiates outward. Removing the white
    // core also kills the hot-spot that the global bloom filter was
    // amplifying into a visible blob.
    const grad = ctx.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0, 'rgba(255,220,140,.5)');
    grad.addColorStop(0.35, 'rgba(255,210,130,.28)');
    grad.addColorStop(0.7, 'rgba(255,200,120,.1)');
    grad.addColorStop(1, 'rgba(255,200,120,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, r * 2, r * 2);
  }
  cachedLightTexture = Texture.from(canvas);
  cachedLightTexture.source.scaleMode = 'linear';
  // Mipmaps OFF for this texture: the sprite is rendered at the same
  // size as the canvas (or close), so we don't need a pyramid, and
  // mipmap generation on a non-power-of-2 canvas with semi-transparent
  // edges was the suspected root cause of the residual square halo.
  cachedLightTexture.source.autoGenerateMipmaps = false;
  return cachedLightTexture;
}

function buildLights(stage: Stage, world: World): void {
  const haloTex = getLightTexture();
  // Reset flicker tracker — caller (startLevel) clears the lights layer
  // children; we have to drop the now-stale Sprite references too.
  stage.lightFlickers.length = 0;
  for (const L of world.lights) {
    // Base soft glow — normal blend, gives the floor its warm wash.
    const base = new Sprite(haloTex);
    base.anchor.set(0.5);
    base.position.set(L.x, L.y);
    const size = L.r * 2;
    base.width = size;
    base.height = size;
    stage.layers.lights.addChild(base);

    // Additive bloom — slightly larger sprite at low alpha. Overlapping
    // with the base in additive mode "stacks" brightness toward the
    // bulb, faking sub-pixel HDR bloom without a render texture. Kept
    // tame so a sprite walking under the lamp still reads — the global
    // AdvancedBloomFilter on worldRoot also lifts these pixels further.
    const bloom = new Sprite(haloTex);
    bloom.anchor.set(0.5);
    bloom.position.set(L.x, L.y);
    bloom.width = bloom.height = L.r * 2;
    bloom.alpha = 0.22;
    bloom.blendMode = 'add';
    stage.layers.lights.addChild(bloom);
    // Hand the bloom sprite to the ticker so it can flicker its alpha.
    stage.lightFlickers.push(bloom);

    // Lamp fixture sprite — the actual industrial fixture image at
    // the lamp source. Normal blend so it reads as a solid mounted
    // object. The bloom filter on worldRoot picks up its bright bulb
    // pixels and gives it a real glow.
    const fixture = new Sprite(tex('lamp'));
    fixture.anchor.set(0.5);
    fixture.position.set(L.x, L.y);
    fixture.width = 28;
    fixture.height = 28;
    stage.layers.lights.addChild(fixture);
  }
}

function buildWallShadows(stage: Stage, world: World): void {
  const g = new Graphics();
  for (const w of world.walls) {
    g.rect(w.x + 3, w.y + w.h, w.w, 8);
  }
  g.fill({ color: 0x000000, alpha: 0.6 });
  stage.layers.wallShadows.addChild(g);
}

function isWall(world: World, tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= world.MW || ty >= world.MH) return false;
  return world.MAP[ty]![tx] === '#';
}

function buildWalls(stage: Stage, world: World): void {
  // Layered Graphics objects, one per visual styling — each batched.
  const body = new Graphics();
  const south = new Graphics();
  const top = new Graphics();
  const topGlow = new Graphics(); // additive thin highlight on the very top
  const inner = new Graphics(); // inner darker rim
  const outline = new Graphics();
  const rivets = new Graphics();

  for (const w of world.walls) {
    body.rect(w.x, w.y, w.w, w.h);
    south.rect(w.x, w.y + w.h - 4, w.w, 4);
    top.rect(w.x, w.y, w.w, 4);
    topGlow.rect(w.x, w.y, w.w, 1.5);
    inner.rect(w.x + 2, w.y + 2, w.w - 4, w.h - 4);
    outline.rect(w.x + 0.5, w.y + 0.5, w.w - 1, w.h - 1);
  }
  body.fill(0x3a3f50);
  south.fill(0x16181f);
  top.fill({ color: 0xffffff, alpha: 0.1 });
  // Razor-thin additive line at the top edge: catches the eye, suggests
  // a light bouncing off the wall lip.
  topGlow.fill({ color: 0xa0c8ff, alpha: 0.4 });
  topGlow.blendMode = 'add';
  inner.stroke({ color: 0x2a2e3a, alpha: 0.6, width: 1 });
  outline.stroke({ color: 0x14161e, width: 1 });

  // Rivets at exterior corners — places where a wall edge meets open floor.
  // Walk every wall tile and drop a rivet near corners adjacent to floor.
  for (let y = 0; y < world.MH; y++) {
    for (let x = 0; x < world.MW; x++) {
      if (!isWall(world, x, y)) continue;
      const px = x * world.TILE;
      const py = y * world.TILE;
      // For each of the 4 corners, place a rivet if both adjacent neighbours are non-wall.
      const corners: Array<[number, number, number, number, number, number]> = [
        // [cornerX, cornerY, nbAx, nbAy, nbBx, nbBy]
        [px + 5, py + 5, x - 1, y, x, y - 1],
        [px + world.TILE - 5, py + 5, x + 1, y, x, y - 1],
        [px + 5, py + world.TILE - 5, x - 1, y, x, y + 1],
        [px + world.TILE - 5, py + world.TILE - 5, x + 1, y, x, y + 1],
      ];
      for (const [cx, cy, ax, ay, bx, by] of corners) {
        if (!isWall(world, ax, ay) && !isWall(world, bx, by)) {
          rivets.circle(cx, cy, 1.2);
        }
      }
    }
  }
  rivets.fill({ color: 0x8a90a4, alpha: 0.7 });

  // Metal-panel texture — drives the wall look. The body fill layer
  // underneath only shows on the edges where the texture might miss
  // a pixel after masking. South/top/outline detail layers overlay on
  // top to keep our procedural lit-edge cues.
  const wallTex = getWallTexture();
  const overlay = new TilingSprite({
    texture: wallTex,
    width: world.WORLD_W,
    height: world.WORLD_H,
  });
  const wScale = WALL_TILE_PX / wallTex.width;
  overlay.tileScale.set(wScale, wScale);
  overlay.alpha = 1;

  const overlayMask = new Graphics();
  for (const w of world.walls) overlayMask.rect(w.x, w.y, w.w, w.h);
  overlayMask.fill(0xffffff);
  overlay.mask = overlayMask;

  // Order matters: body fill → procedural texture (over body) → south +
  // top + topGlow + inner + outline + rivets (final touches on top).
  stage.layers.walls.addChild(
    body,
    overlay,
    overlayMask,
    south,
    top,
    topGlow,
    inner,
    outline,
    rivets,
  );
}
